import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
// AdminLayout is handled by the parent AdminAudioPage
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ListMusic,
  Plus,
  Pencil,
  Trash2,
  X,
  ChevronUp,
  ChevronDown,
  Search,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';

const DEFAULT_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='4' fill='%23374151'/%3E%3Cpath d='M20 44V20l24 12z' fill='%236B7280'/%3E%3C/svg%3E";

const CollectionImage = ({ src }) => {
  const [err, setErr] = useState(false);
  return (
    <img
      src={err || !src ? DEFAULT_IMG : src}
      alt=""
      width={48}
      height={48}
      onError={() => setErr(true)}
      className="rounded object-cover border border-border/40 bg-muted shrink-0"
    />
  );
};

// ── CollectionFormDialog ───────────────────────────────────────────────────────
const CollectionFormDialog = ({ collection, open, onClose, onSaved }) => {
  const { language } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const coverRef = useRef(null);
  const isEdit = !!collection;

  useEffect(() => {
    if (open) {
      setName(collection?.name || '');
      setDescription(collection?.description || '');
      setCover(null);
      setError('');
    }
  }, [open, collection]);

  const handleSave = async () => {
    if (!name.trim()) { setError(t('admin_audio_name_required', language)); return; }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('description', description);
      if (cover) fd.append('cover', cover);
      if (isEdit) {
        await axios.patch(`/api/admin/audio/collections/${collection.id}`, fd);
      } else {
        await axios.post('/api/admin/audio/collections', fd);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('admin_audio_edit_collection', language) : t('admin_audio_new_collection', language)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t('admin_name', language)} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{t('admin_audio_description', language)}</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('admin_audio_description_placeholder', language)} className="mt-1" />
          </div>
          <div>
            <Label>{t('admin_audio_cover_image', language)}</Label>
            <div className="flex gap-2 mt-1">
              <Button variant="outline" size="sm" onClick={() => coverRef.current?.click()}>
                {cover ? cover.name : t('admin_audio_choose_image', language)}
              </Button>
              {cover && (
                <Button variant="ghost" size="sm" onClick={() => setCover(null)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => setCover(e.target.files?.[0] || null)} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('cancel', language)}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('loading', language) : t('save', language)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── TrackPickerDialog ──────────────────────────────────────────────────────────
const TrackPickerDialog = ({ collection, open, onClose, onAdded }) => {
  const { language } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      fetchTracks('');
    }
  }, [open]);

  const fetchTracks = async (q) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/audio/tracks', {
        params: { search: q, page_size: 100, is_active: 'true' },
      });
      setTracks(res.data.tracks || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const existingIds = new Set(collection?.track_ids || []);

  const handleAdd = async (trackId) => {
    setAdding(trackId);
    try {
      await axios.post(`/api/admin/audio/collections/${collection.id}/tracks`, { track_id: trackId });
      onAdded();
    } catch (err) {
      console.error(err);
    } finally {
      setAdding(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin_audio_add_tracks_to', language)} "{collection?.name}"</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); fetchTracks(e.target.value); }}
            placeholder={t('admin_audio_search_tracks', language)}
            className="pl-8"
          />
        </div>
        <ScrollArea className="h-72">
          {loading ? (
            <div className="text-center py-4 text-muted-foreground text-sm">{t('loading', language)}</div>
          ) : (
            <div className="space-y-1 pr-2">
              {tracks.map((tk) => (
                <div
                  key={tk.id}
                  className="flex items-center justify-between gap-2 rounded p-2 hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{tk.episodes || tk.title || tk.id}</p>
                    <div className="flex gap-1 flex-wrap">
                      {(tk.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs px-1 py-0">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  {existingIds.has(tk.id) ? (
                    <Badge variant="secondary" className="shrink-0">{t('admin_audio_in_list', language)}</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={adding === tk.id}
                      onClick={() => handleAdd(tk.id)}
                    >
                      {adding === tk.id ? '...' : <Plus className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              ))}
              {tracks.length === 0 && !loading && (
                <p className="text-center text-muted-foreground py-4">{t('admin_audio_no_tracks', language)}</p>
              )}
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('close', language)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── CollectionCard ─────────────────────────────────────────────────────────────
const CollectionCard = ({ collection, allTracks, onEdit, onDelete, onReorder, onRemoveTrack }) => {
  const { language } = useAuth();
  const trackMap = new Map(allTracks.map((tk) => [tk.id, tk]));

  const move = async (idx, dir) => {
    const ids = [...collection.track_ids];
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    await onReorder(collection.id, ids);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <CollectionImage src={collection.cover_url} />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{collection.name}</CardTitle>
            {collection.description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{collection.description}</p>
            )}
            <p className="text-xs text-muted-foreground">{collection.track_ids?.length || 0} tracks</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => onEdit(collection)} title={t('admin_audio_edit_collection', language)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(collection)} title={t('delete', language)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {(collection.track_ids || []).map((tid, idx) => {
          const tk = trackMap.get(tid);
          return (
            <div key={tid} className="flex items-center gap-2 rounded p-1.5 hover:bg-muted/40 text-sm">
              <div className="flex flex-col gap-0">
                <Button variant="ghost" size="icon" className="h-4 w-4 p-0" onClick={() => move(idx, -1)} disabled={idx === 0}>
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-4 w-4 p-0" onClick={() => move(idx, 1)} disabled={idx === collection.track_ids.length - 1}>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
              <span className="text-xs text-muted-foreground w-5 text-right">{idx + 1}.</span>
              <span className="flex-1 truncate">{tk ? (tk.episodes || tk.title || tid) : tid}</span>
              {!tk && <Badge variant="outline" className="text-xs">{t('admin_audio_not_found', language)}</Badge>}
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemoveTrack(collection.id, tid)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          );
        })}
        {!collection.track_ids?.length && (
          <p className="text-xs text-muted-foreground text-center py-2">{t('admin_audio_collection_empty', language)}</p>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const AdminAudioCollectionsPage = () => {
  const { language } = useAuth();
  const [collections, setCollections] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editCol, setEditCol] = useState(null);
  const [deleteCol, setDeleteCol] = useState(null);
  const [pickerCol, setPickerCol] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [colsRes, tracksRes] = await Promise.all([
        axios.get('/api/admin/audio/collections'),
        axios.get('/api/admin/audio/tracks', { params: { page_size: 500 } }),
      ]);
      setCollections(colsRes.data.collections || []);
      setAllTracks(tracksRes.data.tracks || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteCol) return;
    try {
      await axios.delete(`/api/admin/audio/collections/${deleteCol.id}`);
      setDeleteCol(null);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReorder = async (colId, trackIds) => {
    try {
      const res = await axios.patch(`/api/admin/audio/collections/${colId}/reorder`, { track_ids: trackIds });
      setCollections((prev) => prev.map((c) => (c.id === colId ? res.data : c)));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveTrack = async (colId, trackId) => {
    try {
      await axios.delete(`/api/admin/audio/collections/${colId}/tracks/${trackId}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-cinzel flex items-center gap-2">
              <ListMusic className="h-6 w-6" />
              {t('admin_audio_collections_title', language)}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{collections.length} collection(s)</p>
          </div>
          <Button onClick={() => { setEditCol(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin_audio_new_collection', language)}
          </Button>
        </div>

        {loading ? (
          <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
        ) : (
          <div className="space-y-4">
            {collections.map((col) => (
              <div key={col.id}>
                <CollectionCard
                  collection={col}
                  allTracks={allTracks}
                  onEdit={(c) => { setEditCol(c); setFormOpen(true); }}
                  onDelete={setDeleteCol}
                  onReorder={handleReorder}
                  onRemoveTrack={handleRemoveTrack}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1 ml-0"
                  onClick={() => setPickerCol(col)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {t('admin_audio_add_tracks', language)}
                </Button>
              </div>
            ))}
            {collections.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                {t('admin_audio_no_collections', language)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CollectionFormDialog
        collection={editCol}
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditCol(null); }}
        onSaved={fetchData}
      />

      <TrackPickerDialog
        collection={pickerCol}
        open={!!pickerCol}
        onClose={() => setPickerCol(null)}
        onAdded={fetchData}
      />

      <AlertDialog open={!!deleteCol} onOpenChange={(v) => !v && setDeleteCol(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin_audio_delete_collection_title', language)} "{deleteCol?.name}" ?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin_audio_delete_collection_desc', language)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel', language)}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t('delete', language)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminAudioCollectionsPage;
